import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { Menu, MenuTrigger, MenuPopover, MenuLinks, MenuContent, MenuSection } from './Menu'
import { Text, Strong } from '../Type'
import { Flex, Box } from '../Grid'
import Badge from '../Badge'
import Avatar from '../Avatar'

import Select from '../Forms/Select'

import { useSession } from '../../providers/SessionProvider'

const BADGE_TYPE = {
  admin: 'error',
  member: 'success',
  viewer: 'neutral'
}

const LightDarkControl = () => {
  const [colorScheme, setColorScheme] = useState('light dark')
  const currentColorScheme =
    localStorage.getItem('color-scheme') || document.documentElement.style.colorScheme || 'light dark'

  useEffect(() => {
    updateColorScheme(currentColorScheme)
  }, [currentColorScheme])

  const updateColorScheme = newColorScheme => {
    setColorScheme(newColorScheme)
    localStorage.setItem('color-scheme', newColorScheme)
    document.documentElement.style.colorScheme = newColorScheme
  }

  const options = [
    {
      value: 'light dark',
      label: 'System preference'
    },
    {
      value: 'light',
      label: '🌞 Light'
    },
    {
      value: 'dark',
      label: '🌑 Dark'
    }
  ]

  return <Select heading="Interface theme" value={colorScheme} options={options} onChange={updateColorScheme} />
}

const UserMenu = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { features, currentUser, currentRole: role } = useSession()
  const { avatar, name, email, staff } = currentUser
  const { formatMessage } = useIntl()

  const MENU_LINKS = [
    {
      name: formatMessage({ id: 'navigation.menu.users.profile' }),
      to: '/you/settings/profile',
      forceRefresh: false
    },
    {
      name: formatMessage({ id: 'navigation.menu.users.notifications' }),
      to: '/you/settings/email-notifications',
      forceRefresh: false
    },
    {
      name: formatMessage({ id: 'navigation.menu.users.applications' }),
      to: '/you/settings/applications',
      forceRefresh: false
    },
    {
      name: formatMessage({ id: 'navigation.menu.users.accessTokens' }),
      to: '/you/settings/tokens',
      forceRefresh: false
    },
    {
      name: formatMessage({ id: 'navigation.menu.users.sign_out' }),
      to: '/sign-out',
      forceRefresh: true
    }
  ]
  const label = formatMessage({
    id: `organisations.members.roles.${role}`
  })

  const links = MENU_LINKS.filter(({ feature }) => (feature ? features.includes(feature) : true))

  if (staff) {
    links.splice(0, 0, {
      name: 'Admin',
      to: '/admin',
      forceRefresh: true
    })
  }

  return (
    <Menu onOpenChange={setIsExpanded}>
      <MenuTrigger title={formatMessage({ id: 'navigation.links.user' })}>
        <Avatar
          name={name}
          email={email}
          url={avatar}
          size="medium"
          variant={`${role}Link`}
          className={isExpanded ? 'active' : ''}
        />
      </MenuTrigger>
      <MenuPopover align="end">
        <MenuContent level="sm">
          <MenuSection>
            <Flex alignItems="center">
              <Box mr="8px">
                <Strong color="light-dark(var(--brand-grey-400), var(--brand-grey-200))" fontWeight="bold">
                  {name}
                </Strong>
              </Box>
              <Box lineHeight={1}>
                <Badge type={BADGE_TYPE[role]}>{label}</Badge>
              </Box>
            </Flex>
            <Box>
              <Text color="grey300">{email}</Text>
            </Box>
          </MenuSection>
          <MenuSection>
            <MenuLinks links={links} maxHeight="100%" />
          </MenuSection>
          <MenuSection>
            <LightDarkControl />
          </MenuSection>
        </MenuContent>
      </MenuPopover>
    </Menu>
  )
}

export default UserMenu
